import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Upload,
  FileText,
  Check,
  X,
  AlertTriangle,
  ArrowLeft,
  Loader2,
  User,
  Shield,
  RefreshCw,
  Users,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const certTypeLabels = {
  cpr_aed: "CPR/AED",
  cpr_aed_rte_combo: "AED/CPR/RTE Combo",
  cpr_aed_instructor: "CPR/AED Instructor",
  lifeguard: "Lifeguard",
  wsi: "Water Safety Instructor (WSI)",
  health_nursing: "Health/Nursing",
  boating: "Boating",
  ceu: "CEU/CPR",
  epipen: "Epi Pen/Anaphylaxis",
  bls: "Basic Life Support",
  pal: "Pool Activity Leader",
};

  const statusConfig = {
  uploaded: { icon: Check, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", label: "Matched" },
  unmatched: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200", label: "No Match" },
  error: { icon: X, color: "text-red-600", bg: "bg-red-50", border: "border-red-200", label: "Error" },
  pending: { icon: Loader2, color: "text-slate-400", bg: "bg-slate-50", border: "border-slate-200", label: "Pending" },
  processing: { icon: Loader2, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200", label: "Processing" },
  complete: { icon: Check, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", label: "Complete" },
};

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

export default function BulkUploadPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFilesSelected = (fileList) => {
    const files = Array.from(fileList);
    setSelectedFiles(files);
    setResults(null);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) {
      handleFilesSelected(e.dataTransfer.files);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingJobs, setProcessingJobs] = useState([]);

  const pollJobStatus = async (jobId, filename) => {
    const maxAttempts = 120; // 10 minutes max
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(r => setTimeout(r, 5000)); // poll every 5 seconds
      try {
        const resp = await axios.get(`${API}/upload/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const job = resp.data;
        if (job.status === "complete") {
          return {
            original_filename: filename,
            status: "uploaded",
            staff_matches: (job.results || []).map(r => ({
              staff_id: r.staff_id,
              staff_name: r.staff_name,
              match_score: r.match_score,
              expiration_date: r.expiration_date,
              cert_type: r.cert_type_name || r.cert_type
            })),
            cert_type_match: { id: job.detected_cert_type, name: job.detected_cert_name },
            error: null,
            multi_match: (job.results || []).length > 1,
            async_completed: true,
            total_pages: job.total_pages
          };
        } else if (job.status === "error") {
          return {
            original_filename: filename,
            status: "error",
            staff_matches: [],
            cert_type_match: null,
            error: job.error || "Processing failed"
          };
        }
        // Still processing - update progress
        setProcessingJobs(prev => prev.map(j => 
          j.job_id === jobId ? { ...j, processed_pages: job.processed_pages, total_pages: job.total_pages } : j
        ));
      } catch {
        // Poll failed, try again
      }
    }
    return {
      original_filename: filename,
      status: "error",
      staff_matches: [],
      cert_type_match: null,
      error: "Processing timed out"
    };
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;

    setUploading(true);
    setUploadProgress(0);
    setProcessingJobs([]);
    const allResults = [];
    const asyncJobs = [];
    let totalUploaded = 0;
    let totalUnmatched = 0;
    let totalErrors = 0;
    let totalAssignments = 0;

    // Upload files ONE AT A TIME to avoid proxy size limits
    for (let i = 0; i < selectedFiles.length; i++) {
      setUploadProgress(i + 1);
      const file = selectedFiles[i];
      const formData = new FormData();
      formData.append("files", file);

      try {
        const response = await axios.post(`${API}/upload/bulk`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 180000,
        });
        const data = response.data;
        
        for (const result of (data.results || [])) {
          if (result.status === "processing" && result.job_id) {
            // Async job — will poll later
            asyncJobs.push({ job_id: result.job_id, filename: result.original_filename, total_pages: result.total_pages });
            allResults.push(result);
          } else {
            allResults.push(result);
            if (result.status === "uploaded") totalUploaded++;
            else if (result.status === "unmatched") totalUnmatched++;
            else if (result.status === "error") totalErrors++;
            totalAssignments += (result.staff_matches || []).length;
          }
        }
      } catch (error) {
        // For large files, the upload may succeed but the proxy times out the response
        // Check if a background job was created for this file
        const isTimeout = error.code === "ECONNABORTED" 
          || error.message?.includes("timeout") 
          || error.message?.includes("Network Error")
          || !error.response
          || error.response?.status === 502 
          || error.response?.status === 504
          || error.response?.status === 408;
        
        if (isTimeout) {
          // Wait a moment for the server to create the job
          await new Promise(r => setTimeout(r, 3000));
          try {
            const jobsResp = await axios.get(`${API}/upload/jobs/recent?filename=${encodeURIComponent(file.name)}`, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 10000,
            });
            const recentJob = jobsResp.data;
            if (recentJob && recentJob.id) {
              asyncJobs.push({ job_id: recentJob.id, filename: file.name, total_pages: recentJob.total_pages });
              allResults.push({
                original_filename: file.name,
                status: "processing",
                job_id: recentJob.id,
                total_pages: recentJob.total_pages,
                staff_matches: [],
                cert_type_match: null,
                error: null
              });
              toast.info(`Large file "${file.name}" is being processed in the background...`);
              continue;
            }
          } catch {
            // Couldn't find job either
          }
        }
        allResults.push({
          original_filename: file.name,
          status: isTimeout ? "error" : "error",
          staff_matches: [],
          cert_type_match: null,
          error: isTimeout 
            ? "Upload timed out — the file may still be processing. Check Settings for results in a few minutes."
            : (error.response?.data?.detail || "Upload failed")
        });
        totalErrors++;
      }
    }

    // Show immediate results (including any "processing" placeholders)
    setResults({
      total_files: allResults.length,
      uploaded: totalUploaded,
      unmatched: totalUnmatched,
      errors: totalErrors,
      processing: asyncJobs.length,
      total_assignments: totalAssignments,
      results: allResults
    });
    setProcessingJobs(asyncJobs);
    setUploading(false);

    if (totalUploaded > 0) {
      toast.success(`${totalUploaded} file${totalUploaded !== 1 ? "s" : ""} matched, ${totalAssignments} staff assignments`);
    }
    if (totalUnmatched > 0) {
      toast.warning(`${totalUnmatched} file${totalUnmatched !== 1 ? "s" : ""} could not be matched`);
    }
    if (totalErrors > 0) {
      toast.error(`${totalErrors} file${totalErrors !== 1 ? "s" : ""} had errors`);
    }
    if (asyncJobs.length > 0) {
      toast.info(`${asyncJobs.length} large scan${asyncJobs.length !== 1 ? "s" : ""} processing in background...`);
    }

    // Poll async jobs
    if (asyncJobs.length > 0) {
      const completedResults = await Promise.all(
        asyncJobs.map(j => pollJobStatus(j.job_id, j.filename))
      );

      setResults(prev => {
        if (!prev) return prev;
        const updatedResults = prev.results.map(r => {
          if (r.status === "processing" && r.job_id) {
            const completed = completedResults.find(c => c.original_filename === r.original_filename);
            return completed || r;
          }
          return r;
        });
        
        const newUploaded = updatedResults.filter(r => r.status === "uploaded").length;
        const newUnmatched = updatedResults.filter(r => r.status === "unmatched").length;
        const newErrors = updatedResults.filter(r => r.status === "error").length;
        const newProcessing = updatedResults.filter(r => r.status === "processing").length;
        const newAssignments = updatedResults.reduce((sum, r) => sum + (r.staff_matches || []).length, 0);
        
        return {
          ...prev,
          results: updatedResults,
          uploaded: newUploaded,
          unmatched: newUnmatched,
          errors: newErrors,
          processing: newProcessing,
          total_assignments: newAssignments
        };
      });

      const successCount = completedResults.filter(r => r.status === "uploaded").length;
      if (successCount > 0) {
        const totalMatched = completedResults.reduce((sum, r) => sum + (r.staff_matches || []).length, 0);
        toast.success(`Scan processing complete! ${totalMatched} staff matched from large scans.`);
      }
      setProcessingJobs([]);
    }
  };

  const handleReset = () => {
    setSelectedFiles([]);
    setResults(null);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/staff")}
          className="text-slate-600 hover:text-slate-900 -ml-2"
          data-testid="back-to-staff"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Staff
        </Button>
      </div>

      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Bulk Upload Certifications
        </h1>
        <p className="text-slate-500 mt-1">
          Upload cert files — the system reads each PDF/image to find staff names on the certificate and auto-assigns them.
        </p>
      </div>

      {/* How it works */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="how-it-works">
        <h3 className="text-sm font-semibold text-blue-800 mb-2">How It Works</h3>
        <ul className="text-sm text-blue-700 space-y-1.5">
          <li className="flex items-start gap-2">
            <Eye className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>The system <strong>reads the content</strong> of each PDF/image to find staff member names</span>
          </li>
          <li className="flex items-start gap-2">
            <Users className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Files with <strong>multiple names</strong> are automatically assigned to all matched staff</span>
          </li>
          <li className="flex items-start gap-2">
            <Shield className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Cert type (CPR, Lifeguard, Boating, etc.) is <strong>auto-detected</strong> from file content</span>
          </li>
          <li className="flex items-start gap-2">
            <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>If content can't be read, it falls back to matching by <strong>filename</strong></span>
          </li>
        </ul>
      </div>

      {/* Drop Zone */}
      {!results && (
        <div
          className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer ${
            dragOver
              ? "border-brand-navy bg-brand-navy/5"
              : "border-slate-300 hover:border-slate-400 bg-white"
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          data-testid="bulk-upload-dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFilesSelected(e.target.files)}
            accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.txt,.csv,.xls,.xlsx"
          />
          <Upload className={`h-10 w-10 mx-auto mb-4 ${dragOver ? "text-brand-navy" : "text-slate-400"}`} />
          <p className="text-lg font-medium text-slate-700">
            {dragOver ? "Drop files here" : "Click or drag files here"}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            PDF, images, documents — up to 25MB each
          </p>
        </div>
      )}

      {/* Selected Files Preview */}
      {selectedFiles.length > 0 && !results && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">
                {selectedFiles.length} file{selectedFiles.length !== 1 ? "s" : ""} selected
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Total: {formatBytes(selectedFiles.reduce((sum, f) => sum + f.size, 0))}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReset} data-testid="clear-files-button">
                Clear
              </Button>
              <Button
                size="sm"
                onClick={handleUpload}
                disabled={uploading}
                className="bg-brand-navy hover:bg-brand-navy/90"
                data-testid="start-bulk-upload-button"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing {uploadProgress} of {selectedFiles.length}...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload & Match
                  </>
                )}
              </Button>
            </div>
          </div>
          <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {selectedFiles.map((file, idx) => (
              <div key={idx} className="px-5 py-3 flex items-center gap-3">
                <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-700 truncate flex-1">{file.name}</span>
                <span className="text-xs text-slate-400">{formatBytes(file.size)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-4">
          {/* Summary Bar */}
          <div className="grid grid-cols-5 gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-slate-700">{results.total_files}</p>
              <p className="text-xs text-slate-500">Files</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center" data-testid="result-uploaded-count">
              <p className="text-xl font-bold text-emerald-700">{results.uploaded}</p>
              <p className="text-xs text-emerald-600">Matched</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center" data-testid="result-assignments-count">
              <p className="text-xl font-bold text-blue-700">{results.total_assignments}</p>
              <p className="text-xs text-blue-600">Staff Assigned</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center" data-testid="result-unmatched-count">
              <p className="text-xl font-bold text-amber-700">{results.unmatched}</p>
              <p className="text-xs text-amber-600">Unmatched</p>
            </div>
            {(results.processing > 0) && (
              <div className="bg-blue-50 border border-blue-300 rounded-lg p-3 text-center animate-pulse" data-testid="result-processing-count">
                <p className="text-xl font-bold text-blue-700">{results.processing}</p>
                <p className="text-xs text-blue-600">Scanning...</p>
              </div>
            )}
          </div>

          {/* File Results List */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Upload Results</h3>
              <Button variant="outline" size="sm" onClick={handleReset} data-testid="upload-more-button">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Upload More
              </Button>
            </div>
            <div className="divide-y divide-slate-100">
              {results.results.map((r, idx) => {
                const cfg = statusConfig[r.status] || statusConfig.pending;
                const StatusIcon = cfg.icon;
                const staffMatches = r.staff_matches || [];
                const isProcessing = r.status === "processing";
                const processingJob = processingJobs.find(j => j.job_id === r.job_id);

                return (
                  <div
                    key={idx}
                    className={`px-5 py-4 ${cfg.bg}`}
                    data-testid={`bulk-result-${idx}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`h-8 w-8 rounded-full ${cfg.bg} border ${cfg.border} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <StatusIcon className={`h-4 w-4 ${cfg.color} ${isProcessing ? 'animate-spin' : ''}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <FileText className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-slate-800 truncate">
                            {r.original_filename}
                          </span>
                          {r.multi_match && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                              Multi-cert
                            </span>
                          )}
                          {r.total_pages && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                              {r.total_pages} pages
                            </span>
                          )}
                        </div>
                        
                        {/* Processing indicator */}
                        {isProcessing && (
                          <div className="mt-2">
                            <p className="text-xs text-blue-600 font-medium">
                              Scanning pages with OCR... {processingJob?.processed_pages ? `${processingJob.processed_pages}/${processingJob.total_pages}` : 'starting'}
                            </p>
                            <div className="w-full bg-blue-100 rounded-full h-1.5 mt-1">
                              <div 
                                className="bg-blue-500 h-1.5 rounded-full transition-all duration-500" 
                                style={{ width: processingJob?.total_pages ? `${(processingJob.processed_pages / processingJob.total_pages * 100)}%` : '5%' }}
                              />
                            </div>
                          </div>
                        )}

                        {/* Cert type */}
                        {r.cert_type_match && !isProcessing && (
                          <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500">
                            <Shield className="h-3 w-3" />
                            <span>Detected: {r.cert_type_match.name}</span>
                          </div>
                        )}
                        
                        {/* Staff matches */}
                        {staffMatches.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {staffMatches.map((m, mIdx) => (
                              <span
                                key={mIdx}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200"
                              >
                                <User className="h-3 w-3" />
                                {m.staff_name}
                                {m.cert_type && (
                                  <span className="text-emerald-600">({m.cert_type})</span>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Async completed info */}
                        {r.async_completed && (
                          <p className="text-xs text-emerald-600 mt-1 font-medium">
                            Large scan processed — {staffMatches.length} staff matched from {r.total_pages} pages
                          </p>
                        )}
                        
                        {/* Error / unmatched info */}
                        {r.error && (
                          <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                            {r.error}
                          </p>
                        )}

                        {/* Per-match failures (partial failure) */}
                        {r.failed_matches && r.failed_matches.length > 0 && (
                          <div className="mt-2 bg-red-50 border border-red-200 rounded p-2">
                            <p className="text-xs font-semibold text-red-800 mb-1">
                              {r.failed_matches.length} assignment{r.failed_matches.length !== 1 ? 's' : ''} failed:
                            </p>
                            <ul className="text-xs text-red-700 space-y-0.5">
                              {r.failed_matches.map((fm, i) => (
                                <li key={i} className="break-words">
                                  • {fm.staff_name}: {fm.error}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {r.extracted_text_preview && r.status === "unmatched" && (
                          <details className="mt-2">
                            <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                              Extracted text preview
                            </summary>
                            <p className="text-xs text-slate-400 mt-1 bg-white rounded p-2 border border-slate-200 break-words">
                              {r.extracted_text_preview}
                            </p>
                          </details>
                        )}
                      </div>

                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.border} ${cfg.color} ${cfg.bg} flex-shrink-0`}>
                        {r.status === "uploaded" && staffMatches.length > 1
                          ? `${staffMatches.length} matched`
                          : isProcessing ? "Scanning..." : cfg.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
